// @flow
import React from 'react';
import { Percentage2DPCellRendererForStaticTable } from '@components/views/grid-utils/renderers';
import { convertDimArrayToDimsKey } from '@components/custom-discounts/mld-for-field/utils';
import { NotificationToastWithTable } from '@presentational/NotificationToastWithTable';
import {
  getCartesianProductOfGivenArrays,
  getNonNullValuesFromObject,
} from '@src/common-utils/utils';

/**
 * Example:
 *   { provider: "aws", cluster_type: "enterprise", connector_sku: null }
 *   → "aws-enterprise" (null values are ignored)
 */
const dimensionsObjectRenderer = (dimensionsObject) => {
  if (!dimensionsObject) {
    return '—';
  }

  const nonNullValues = getNonNullValuesFromObject(dimensionsObject);

  if (nonNullValues.length === 0) {
    return '—';
  }

  const hasAnyArray = nonNullValues.some((value) => Array.isArray(value));

  // In this case, we only have simple values, e.g. [ "aws" , "mz", "basic", "internet" ]
  // So, we can just concat them and render
  if (!hasAnyArray) {
    return convertDimArrayToDimsKey(nonNullValues, ';;');
  }

  // In this case, there could be some values which are arrays
  // E.g., [   ["aws", "gcp"]   , "mz" , ["C1", "C2"]   ]

  // We normalize everything into an array and then take the cartesian product and create the lines
  const arrays = nonNullValues.map((value) => {
    return Array.isArray(value) ? value : [value];
  });

  const combinations = getCartesianProductOfGivenArrays(arrays);
  const lines = combinations.map((combo) => convertDimArrayToDimsKey(combo, ';;'));

  return lines.join('\n');
};

const getTableContents = (failedMetrics) => {
  const headerRow = ['Metric', 'Dimensions', 'Discount'];

  const dataRows = failedMetrics.map((item) => [
    item.metric_name,
    item.dimension,
    item.discount_value,
  ]);

  return { headerRow, dataRows };
};

const getCellRenderers = () => [
  (cellValue) => cellValue, // For Metric Name
  (cellValue) => dimensionsObjectRenderer(cellValue), // For Dimensions
  (cellValue) => Percentage2DPCellRendererForStaticTable(cellValue), // For Discount
];

export const ImportDiscountsNotification = ({ succeededMetrics, failedMetrics }) => {
  const succeededMetricsCount = succeededMetrics.length;
  const failedMetricsCount = failedMetrics.length;
  const totalMetricsCount = succeededMetricsCount + failedMetricsCount;

  const message = `${failedMetricsCount} out of ${totalMetricsCount} discount line items could not be imported.\nDetails below::`;

  return (
    <NotificationToastWithTable
      cellRenderers={getCellRenderers()}
      message={message}
      tableContents={getTableContents(failedMetrics)}
    />
  );
};
